'use client';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Card } from '@startuptools/ui/Card';
import NewspaperIcon from '@mui/icons-material/NewspaperOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import HandshakeIcon from '@mui/icons-material/HandshakeOutlined';
import EqualizerIcon from '@mui/icons-material/EqualizerOutlined';
import { Trans } from '@lingui/react';
import { type ReactNode, type PropsWithChildren } from 'react';
import { Typography } from '@startuptools/ui/Typography';

const contentPlatform = [
  {
    icon: <AssignmentTurnedInIcon />,
    header: <Trans id="Share Registry - Get started for free" />,
    text: (
      <Trans id="Intuitive to work with and integrated with the Swedish Companies Registration Office (Bolagsverket)." />
    ),
  },
  {
    icon: <GroupsIcon />,
    header: <Trans id="Board portal - Secure and easy to use" />,
    text: (
      <Trans id="Manage the work from scheduling and invitations all the way to signed minutes directly in the platform." />
    ),
  },
  {
    icon: <HandshakeIcon />,
    header: <Trans id="Stock Options - Easy to get right" />,
    text: <Trans id="With our simple process, issue stock options in less than 15 minutes from idea to e-signing." />,
  },
  {
    icon: <EqualizerIcon />,
    header: <Trans id="Investments - Does not need to be difficult" />,
    text: <Trans id="Make new share issues and funding rounds really simple. Supporting documents & forms included." />,
  },
] as const;

const contentShportal = [
  {
    icon: <NewspaperIcon />,
    header: <Trans id="View your stock ownership" />,
    text: <Trans id="View and follow your ownership and value of your shares over time in all your companies." />,
  },
  {
    icon: <ReceiptLongIcon />,
    header: <Trans id="View your stockoptions" />,
    text: <Trans id="View and follow your vesting. Compare your strike price to the current value of the shares." />,
  },
] as const;

export const SignupLayout = ({
  contentType = 'platform',
  logo,
  children,
  maxHeight,
  inDialog,
}: PropsWithChildren<{
  contentType?: 'platform' | 'shportal';
  logo?: ReactNode;
  maxHeight?: React.ComponentProps<typeof Stack>['maxHeight'];
  inDialog?: boolean;
}>) => {
  const content = contentType === 'platform' ? contentPlatform : contentShportal;

  return (
    <Stack sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: inDialog ? '20px' : undefined }}>
      <Stack
        direction={{ xs: 'column-reverse', md: 'row' }}
        alignSelf="center"
        p={{ xs: 2, md: 4 }}
        width="100%"
        maxWidth={1060}
        gap={{ xs: 6, md: 12 }}
        maxHeight={maxHeight}
      >
        <Stack sx={{ flex: '1 1' }} mt={4}>
          <Stack alignSelf="center" gap={2} maxWidth={380}>
            <Box display={{ xs: 'none', md: 'block' }}>{logo}</Box>
            {content.map((c, i) => (
              <Stack key={i} direction="row" gap={1}>
                {c.icon}
                <Stack gap={1}>
                  <Typography level="title-lg">{c.header}</Typography>
                  <Typography level="body-sm">{c.text}</Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack sx={{ flex: '1 1', justifyContent: 'center', alignItems: 'center' }}>
          <Card
            sx={{
              backgroundColor: 'white',
              minWidth: { xs: '100%', sm: 450 },
              height: { xs: inDialog ? '400px' : '95dvh', sm: 600 },
              p: { xs: 3, md: 4 },
            }}
          >
            <Stack flexGrow={1} justifyContent="space-between">
              {children}
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </Stack>
  );
};
